/**
 * spaceEnemyPools.ts
 * Per-kind object pools and pool-aware acquire/release helpers for the
 * World 2 space enemies.
 *
 * Split out of `spaceEnemySystem.ts` (which kept growing as new enemy
 * kinds landed) so the spawner / update / collision logic stays under the
 * AGENTS.md 300-line file limit. This module owns:
 *   - One `ObjectPool` per enemy kind, prewarmed at module load.
 *   - `SPAWN_SPEC` table mapping each kind to its vertical reach,
 *     spawn-Y inset, and pool `acquire()` thunk.
 *   - `releaseEnemy()` with an exhaustive `never` guard so a new
 *     `SpaceEnemyKind` without a pool mapping is a build error.
 *   - Test-only `_<kind>PoolAvailable()` helpers re-exported by
 *     `spaceEnemySystem` so the existing test API does not change.
 *
 * NO React imports, NO Skia imports, NO async — engine-internal only.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module spaceEnemyPools
 */

import { FighterDrone } from '../entities/FighterDrone';
import { BomberDrone } from '../entities/BomberDrone';
import { ShieldSatellite } from '../entities/ShieldSatellite';
import { StarEater } from '../entities/StarEater';
import type { SpaceEnemyBase } from '../entities/SpaceEnemyBase';
import { ObjectPool } from '../utils/objectPool';
import {
  SPACE_ENEMY_MAX_ACTIVE,
  FIGHTER_DRONE_HEIGHT,
  FIGHTER_DRONE_WAVE_AMPLITUDE_PX,
  BOMBER_DRONE_HEIGHT,
  BOMBER_DRONE_WAVE_AMPLITUDE_PX,
  SHIELD_SATELLITE_HEIGHT,
  SHIELD_SATELLITE_WOBBLE_AMPLITUDE,
  STAR_EATER_HEIGHT,
  type SpaceEnemyKind,
} from '../../constants/spaceEnemyConstants';

/**
 * Module-singleton fighter-drone pool — pre-warmed at module load. Each
 * subclass owns its own pool so per-class `reset()` contracts stay
 * isolated. The `SPACE_ENEMY_MAX_ACTIVE` cap is checked in the spawner
 * against `spaceState.enemies.length` (which mixes all kinds) so the
 * total enemy budget is capped regardless of which kind dominates.
 */
const fighterPool = new ObjectPool<FighterDrone>(
  () => new FighterDrone(),
  SPACE_ENEMY_MAX_ACTIVE,
);
fighterPool.prewarm(SPACE_ENEMY_MAX_ACTIVE);

/** Sibling pool for `BomberDrone`. See `fighterPool` for the rationale. */
const bomberPool = new ObjectPool<BomberDrone>(
  () => new BomberDrone(),
  SPACE_ENEMY_MAX_ACTIVE,
);
bomberPool.prewarm(SPACE_ENEMY_MAX_ACTIVE);

/** Sibling pool for `ShieldSatellite`. See `fighterPool` for the rationale. */
const satellitePool = new ObjectPool<ShieldSatellite>(
  () => new ShieldSatellite(),
  SPACE_ENEMY_MAX_ACTIVE,
);
satellitePool.prewarm(SPACE_ENEMY_MAX_ACTIVE);

/** Sibling pool for `StarEater`. See `fighterPool` for the rationale. */
const starEaterPool = new ObjectPool<StarEater>(
  () => new StarEater(),
  SPACE_ENEMY_MAX_ACTIVE,
);
starEaterPool.prewarm(SPACE_ENEMY_MAX_ACTIVE);

/**
 * Per-kind spawn metadata. `verticalReach` is the full vertical excursion
 * (hitbox + any wobble amplitude) so a max-roll Y never pushes the entity
 * past the floor margin. `yMinInset` lifts the Y floor for kinds whose
 * orbit can arc upward past their spawn Y (satellite).
 */
export const SPAWN_SPEC: Record<
  SpaceEnemyKind,
  { verticalReach: number; yMinInset: number; acquire: () => SpaceEnemyBase | null }
> = {
  fighter: {
    // DECISION: Include the sine wave amplitude in the reach + inset so a
    // bottom-clamped fighter's downward sample never crosses the floor
    // margin and a top-clamped fighter's upward sample never crosses the
    // HUD strip. Matches the satellite pattern (PR #124 review).
    verticalReach: FIGHTER_DRONE_HEIGHT + FIGHTER_DRONE_WAVE_AMPLITUDE_PX,
    yMinInset: FIGHTER_DRONE_WAVE_AMPLITUDE_PX,
    acquire: () => fighterPool.acquire(),
  },
  bomber: {
    // DECISION: Same reach inflation as fighter — see above.
    verticalReach: BOMBER_DRONE_HEIGHT + BOMBER_DRONE_WAVE_AMPLITUDE_PX,
    yMinInset: BOMBER_DRONE_WAVE_AMPLITUDE_PX,
    acquire: () => bomberPool.acquire(),
  },
  satellite: {
    verticalReach: SHIELD_SATELLITE_HEIGHT + SHIELD_SATELLITE_WOBBLE_AMPLITUDE,
    yMinInset: SHIELD_SATELLITE_WOBBLE_AMPLITUDE,
    acquire: () => satellitePool.acquire(),
  },
  starEater: {
    verticalReach: STAR_EATER_HEIGHT,
    yMinInset: 0,
    acquire: () => starEaterPool.acquire(),
  },
};

/**
 * Release an enemy back to the appropriate subclass pool.
 *
 * Exhaustiveness guard: if a new `SpaceEnemyKind` is added without a
 * matching pool release here, the `_exhaustive: never` assignment fails
 * the build. Without it the function would silently drop the enemy from
 * the active list without returning it to any pool — a slow leak.
 *
 * @param e - Live or dead enemy instance.
 */
export function releaseEnemy(e: SpaceEnemyBase): void {
  switch (e.kind) {
    case 'fighter':
      fighterPool.release(e as FighterDrone);
      return;
    case 'bomber':
      bomberPool.release(e as BomberDrone);
      return;
    case 'satellite':
      satellitePool.release(e as ShieldSatellite);
      return;
    case 'starEater':
      starEaterPool.release(e as StarEater);
      return;
    default: {
      const _exhaustive: never = e.kind;
      throw new Error(`releaseEnemy: unhandled enemy kind ${String(_exhaustive)}`);
    }
  }
}

/**
 * Test-only helper: number of fighter drones currently parked in the pool.
 *
 * @returns Count of available pooled instances.
 * @internal
 */
export function _fighterPoolAvailable(): number {
  return fighterPool.available;
}

/**
 * Test-only helper: number of bomber drones currently parked in the pool.
 *
 * @returns Count of available pooled instances.
 * @internal
 */
export function _bomberPoolAvailable(): number {
  return bomberPool.available;
}

/**
 * Test-only helper: number of shield satellites currently parked in the pool.
 *
 * @returns Count of available pooled instances.
 * @internal
 */
export function _satellitePoolAvailable(): number {
  return satellitePool.available;
}

/**
 * Test-only helper: number of star-eater scouts currently parked in the pool.
 *
 * @returns Count of available pooled instances.
 * @internal
 */
export function _starEaterPoolAvailable(): number {
  return starEaterPool.available;
}
